import { useSelector } from 'react-redux';
import { filterSelector } from '../store/selectors/filters';
import { CountySelection } from './CountySelection';
import { ExportTabTooltip } from './map/Tooltips';
import { DataTable } from './DataTable';
import { LayerSelection } from './LayerSelection';
import { Resources } from './Resources';
import { trackEvent, NAVIGATION } from '../utils/googleAnalytics';
import clsx from 'clsx';

export const MapSidebar = ({
  layerManager,
  structureData,
  availableLayers,
  setActiveTab,
  activeTab,
}) => {
  const filterInfo = useSelector(filterSelector);
  const isCountySelected = !!filterInfo.selectedCounty;

  const tabStyle =
    'px-4 py-1 h-10 rounded-t-md transition-all w-32 flex justify-center items-center no-underline text-base font-semibold';
  const activeTabStyle = 'bg-iceGrey';
  const inactiveTabStyle = 'bg-fossilGrey hover:brightness-105 ';
  const disabledTabStyle = 'bg-fossilGrey cursor-not-allowed';

  return (
    <div className="flex h-full flex-col">
      <div className="z-[1000] flex h-full transform flex-col transition-transform duration-300 ease-in-out">
        <div className="flex gap-2">
          <button
            onClick={() => {
              trackEvent(NAVIGATION, 'map tab');
              setActiveTab('map');
            }}
            className={clsx(tabStyle, activeTab === 'map' ? activeTabStyle : inactiveTabStyle)}
          >
            MAP
          </button>
          <button
            onClick={() => {
              trackEvent(NAVIGATION, 'export tab');
              setActiveTab('export');
            }}
            disabled={!isCountySelected}
            {...(!isCountySelected && {
              'data-tooltip-id': 'export-tab-tooltip',
              'aria-label': 'Export tab disabled until county is selected for export',
            })}
            className={clsx(
              tabStyle,
              !isCountySelected
                ? disabledTabStyle
                : activeTab === 'export'
                  ? activeTabStyle
                  : inactiveTabStyle,
            )}
          >
            DOWNLOAD
          </button>
          <button
            onClick={() => {
              trackEvent(NAVIGATION, 'resources tab');
              setActiveTab('resources');
            }}
            className={clsx(
              tabStyle,
              activeTab === 'resources' ? activeTabStyle : inactiveTabStyle,
            )}
          >
            RESOURCES
          </button>

          {!isCountySelected && <ExportTabTooltip tooltipId="export-tab-tooltip" />}
        </div>

        {activeTab === 'map' && (
          <div className="flex flex-1 flex-col overflow-y-auto rounded-b-md rounded-tr-md bg-iceGrey p-4">
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-800">Map Configuration</h3>
              <p className="m-0 text-xs">NOTE: Layers take a moment to load</p>
            </div>
            <CountySelection layerManager={layerManager} />
            <div className="my-6 flex items-center gap-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-blue bg-teal text-lg font-bold text-blue">
                2
              </div>
              <h3 className="text-lg font-bold">Select and add layers to map</h3>
            </div>
            <div className="flex-1 overflow-hidden">
              <LayerSelection
                layerManager={layerManager}
                availableLayers={availableLayers}
                structureData={structureData}
              />
            </div>
          </div>
        )}

        {activeTab === 'export' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-md rounded-tr-md bg-iceGrey p-4">
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-800">Export</h3>
            <div
              className="flex flex-col justify-between gap-6"
              style={{ height: 'calc(100% - 40px)' }}
            >
              <DataTable layerManager={layerManager} />
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-md rounded-tr-md bg-iceGrey p-4">
            <h3 className="mb-4 text-xl font-semibold text-gray-800">Resources</h3>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <Resources />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
